// @ts-check

import { api } from '@/api'
import { initiateFetchingBankTransactions } from '@/api/bankConnections'
import { getPersonalAccessToken, getToken } from '@/api/oauth'
import {
  LOCAL_STORAGE_KEY_ACCESS_TOKEN,
  LOCAL_STORAGE_KEY_LANDING_PAGE,
  LOCAL_STORAGE_KEY_REFRESH_TOKEN,
  LOCAL_STORAGE_KEY_SESSION_EXPIRATION_TIME,
  LOCAL_STORAGE_KEY_SESSION_IDENTIFIER
} from '@/core/constants'
import { router } from '@/router'
import { addSeconds, isDate, parseISO } from 'date-fns'
import moment from 'moment'
import { getActivePinia } from 'pinia'

/**
 * @returns {AuthModuleState}
 */
export const initAuthState = () => ({
  accessToken: localStorage.getItem(LOCAL_STORAGE_KEY_ACCESS_TOKEN),
  refreshToken: localStorage.getItem(LOCAL_STORAGE_KEY_REFRESH_TOKEN),
  sessionExpirationTime: localStorage.getItem(
    LOCAL_STORAGE_KEY_SESSION_EXPIRATION_TIME
  ),
  sessionIdentifier: localStorage.getItem(LOCAL_STORAGE_KEY_SESSION_IDENTIFIER),
  companyId: null,
  sessionExpirationDialogOpen: false
})

/**
 * @type {AuthGetterTree}
 */
export const authGetters = {
  isLoggedIn: (state, _, rootState) => {
    if (rootState.mobileData?.isMobileWrapperUser) {
      return !!state.accessToken
    }
    return !!state.accessToken && !!state.refreshToken
  },

  tokenExpired: (state) => {
    if (state.sessionExpirationTime) {
      return moment().isSameOrAfter(new Date(state.sessionExpirationTime))
    }
    return false
  },

  sessionExpirationDialogOpen: (state) => {
    return state.sessionExpirationDialogOpen
  }
}

/**
 * @type {AuthMutationTree}
 */
export const authMutations = {
  setSessionIdentifier: (state, identifier) => {
    state.sessionIdentifier = identifier
    localStorage.setItem(LOCAL_STORAGE_KEY_SESSION_IDENTIFIER, identifier)
  },

  setAccessToken: (state, token) => {
    state.accessToken = token
    localStorage.setItem(LOCAL_STORAGE_KEY_ACCESS_TOKEN, token)
  },

  setRefreshToken: (state, token) => {
    state.refreshToken = token
    localStorage.setItem(LOCAL_STORAGE_KEY_REFRESH_TOKEN, token)
  },

  setSessionExpirationTime: (state, expirationSeconds) => {
    /* Default access token gets expiration time as seconds until expiration,
    so the expiration time needs to be calculated.
    Personal access tokens (currently used for mobile users) get an expiration
    time as a timestamp. */
    let isValueDate = true

    // If value comes from localStorage item in mobile application it's already a string
    if (typeof expirationSeconds !== 'string') {
      isValueDate = isDate(expirationSeconds)
    }

    let expirationTime = expirationSeconds

    if (!isValueDate) {
      expirationTime = addSeconds(new Date(), expirationSeconds)
    }

    state.sessionExpirationTime = expirationTime
    localStorage.setItem(
      LOCAL_STORAGE_KEY_SESSION_EXPIRATION_TIME,
      expirationTime
    )
  },

  clearAllData: (state) => {
    state.accessToken = null
    state.refreshToken = null
    state.companyId = null
    state.sessionIdentifier = null
    state.sessionExpirationDialogOpen = false
    localStorage.removeItem(LOCAL_STORAGE_KEY_ACCESS_TOKEN)
    localStorage.removeItem(LOCAL_STORAGE_KEY_REFRESH_TOKEN)
    localStorage.removeItem(LOCAL_STORAGE_KEY_SESSION_EXPIRATION_TIME)
    localStorage.removeItem(LOCAL_STORAGE_KEY_SESSION_IDENTIFIER)
  },

  setCompanyId: (state, id) => {
    state.companyId = id
  },

  setSessionExpirationDialogOpen: (state, open) => {
    state.sessionExpirationDialogOpen = open
  }
}

/**
 * @type {AuthActionTree}
 */
export const authActions = {
  login: async ({ commit, dispatch, rootState }, payload) => {
    const { username, password } = payload

    const { data } = await getToken(username, password)
    commit('setAccessToken', data.access_token)
    commit('setRefreshToken', data.refresh_token)
    commit('setSessionExpirationTime', data.expires_in)
    await dispatch('companyData/init', null, { root: true })

    if (rootState?.mobileData?.isMobileWrapperUser) {
      await dispatch('getPersonalAccessToken', data.access_token)
      return
    }

    const landingPage = localStorage.getItem(LOCAL_STORAGE_KEY_LANDING_PAGE)
    localStorage.removeItem(LOCAL_STORAGE_KEY_LANDING_PAGE)

    // Fetch EB bank transactions in parallel if the company meets the requirements
    if (
      rootState.companyData.company?.can_create_entries &&
      rootState.companyData.company?.is_psd2_connected
    ) {
      // eslint-disable-next-line promise/prefer-await-to-then
      initiateFetchingBankTransactions().catch(() => {
        // No need to handle this error.
      })
    }

    try {
      await router.push({ name: landingPage ?? 'dashboard' })
    } catch {
      await router.push({ name: 'dashboard' })
    }
  },

  getPersonalAccessToken: async (
    { commit, dispatch, rootGetters },
    accessToken
  ) => {
    try {
      const { data } = await getPersonalAccessToken(
        `mobile_pat_${rootGetters['companyData/companyId']}`,
        accessToken
      )
      commit('clearAllData')
      commit('setAccessToken', data.accessToken)
      commit('setSessionExpirationTime', parseISO(data.token.expires_at))
      commit('mobileData/setPersonalAccessTokenId', data.token.id, {
        root: true
      })
      await dispatch('mobileData/setMobileWrapperTokens', null, { root: true })
      await dispatch('companyData/init', null, { root: true })

      await router.push({ name: 'dashboard' })
    } catch (e) {
      commit('clearAllData')
      commit('companyData/clearAllData', null, { root: true })
      throw e
    }
  },

  logout: async ({ state, commit, dispatch, rootGetters }, payload) => {
    const personalAccessTokenId =
      rootGetters['mobileData/personalAccessTokenId']

    // If mobile user, delete mobile tokens
    if (personalAccessTokenId) {
      try {
        await dispatch(
          'mobileData/removePersonalAccessToken',
          {
            patId: personalAccessTokenId,
            accessToken: state.accessToken
          },
          { root: true }
        )
      } catch (error) {
        // We don't want to block logout if mobile token deletion fails in backend
        console.error(error)
      } finally {
        dispatch('mobileData/clearMobileWrapperTokens', null, {
          root: true
        })
      }
    }

    // API logout
    try {
      // Need to ignore validations because otherwise the interceptor will
      // get stuck in a loop.
      await api.post('logout', undefined, { validateStatus: null })
    } catch {
      // Do not handle this for now.
    }

    commit('clearAllData')

    // Navigate to login page
    try {
      await router.push({ name: 'login', query: { message: payload?.message } })
    } catch {
      // Ignore navigation errors
    }

    commit('utils/clearAllData', null, { root: true })
    commit('companyData/clearAllData', null, { root: true })
    commit('expenses/clearAllData', null, { root: true })
    commit('incomes/clearAllData', null, { root: true })
    commit('reports/clearAllData', null, { root: true })
    commit('actions/clearAllData', null, { root: true })
    commit('customers/clearAllData', null, { root: true })
    commit('dashboard/clearAllData', null, { root: true })
    commit('account-transactions/clearAllData', null, { root: true })
    commit('ukkoturva/clearAllData', null, { root: true })

    const activePinia = getActivePinia()

    if (activePinia) {
      // @ts-expect-error - Pinia internal _s property not in public types
      // eslint-disable-next-line no-underscore-dangle
      activePinia._s.forEach((store) => store.$reset())
    }
  }
}

export const authModule = {
  namespaced: true,
  state: initAuthState,
  getters: authGetters,
  mutations: authMutations,
  actions: authActions
}

export default authModule
